/**
 * Mouse + keyboard control.
 *
 *   left click   attack the clicked land (or place a building / ping / spawn)
 *   left drag    box-select your units
 *   right click  move selected units
 *   middle drag  pan          wheel  zoom          WASD/arrows  pan
 */

import {
  ART, MAP_H, MAP_W, Phase, RACE_POWER_AREA_MAX, RACE_POWER_AREA_MIN, RACE_POWER_AREA_STEP,
  RACE_POWER_RANGE, RES_COUNT,
  StructureType, Terrain, UNIT_DEFS, UnitType, WALL_MAX_PER_ORDER, WallKind, idx, priceWithGold,
  racialUnits, wallCost,
} from '../shared/constants';
import type { Race } from '../shared/constants';
import type { Camera } from './camera';
import { net } from './net';
import { noteTouch, touchEcho } from './platform';
import { isFriendly, state } from './state';
import { sfx } from './sound';
import {
  closeAidPanel, closeRadial, dropTool, exitFullscreen, getPingMode, notice, openAidPanel,
  openRadial, radialOpen, setPingMode, toggleBoatMode, toggleCinematic, toggleFullscreen,
  toggleSettings, toggleSpareBuildings, toggleWallMode, trainUnit,
} from './ui';

export const keys = new Set<string>();

/**
 * Queue whichever of *your people's* units carries this letter, if any.
 *
 * The letters live on the unit definitions, beside the name the button shows,
 * so the key and the label can never drift apart — and adding a people means
 * adding its roster, not editing a switch here as well.
 */
function trainByHotkey(letter: string): boolean {
  const race = (state.players.get(state.you)?.race ?? state.race) as Race;
  for (const type of racialUnits(race)) {
    if ((UNIT_DEFS[type].hotkey || '').toLowerCase() === letter) { trainUnit(type); return true; }
  }
  return false;
}

const $ = (id: string) => document.getElementById(id)!;

/** How long two clicks may be apart and still count as a double click. */
const DOUBLE_CLICK_MS = 380;

/**
 * How long a finger must rest before a drag becomes a selection box.
 *
 * Long enough that scrolling the map never trips it by accident, short enough
 * that it does not feel like the game has stopped responding. A fingertip also
 * wobbles: `TOUCH_SLOP` is how far it may drift and still count as held still,
 * and as "a tap rather than a drag" — the same number does both, because they
 * are the same question asked at two moments.
 */
const TOUCH_HOLD_MS = 320;
const TOUCH_SLOP = 12;

/** How close the map draws in on the homeland you have just chosen. */
export const SPAWN_ZOOM = 2.1;   // halved with ART — see shared/constants.ts

export function setupInput(canvas: HTMLCanvasElement, camera: Camera) {
  const selBox = document.createElement('div');
  selBox.style.cssText =
    'position:fixed;border:1px solid #9ff2a0;background:rgba(159,242,160,0.12);pointer-events:none;display:none;z-index:25';
  document.body.appendChild(selBox);

  let dragging = false;
  let panning = false;
  let moved = false;
  let sx = 0, sy = 0, lx = 0, ly = 0;
  let cursor = { x: 0, y: 0 };
  /** laying wall: the drag is a drawing gesture, not a selection box */
  let wallDragging = false;
  let lastWallX = -1, lastWallY = -1;

  /*
   * ---------------------------------------------------------------- touch
   *
   * **A finger has one button and no hover, so the gestures have to carry more.**
   *
   * A mouse can say four different things at the same point — left, right,
   * middle, and "I am merely looking at this". A finger says one. So the map
   * splits the load across *time* instead of across buttons:
   *
   *   drag           pan the world — the thing you do most, so it is the
   *                  gesture that costs nothing to start
   *   tap            with units in hand, send them there; with nothing in hand,
   *                  open the wheel — which is where building, recruiting and
   *                  attacking all live already, so the right-click menu becomes
   *                  the *only* menu and nothing had to be invented for it
   *   press and hold then drag, box-select
   *
   * Hold-then-drag is the one that needed thinking about. Selecting units is the
   * gesture that most wants a drag, and panning had already taken drag — so the
   * hold is the modifier, and it is deliberately the *rarer* action that pays
   * the extra half second.
   */
  let touchHoldTimer: number | null = null;
  /** the hold fired: this drag draws a selection box rather than panning */
  let touchSelecting = false;
  /** a touch that has moved far enough to be a drag rather than a tap */
  let touchMoved = false;

  /**
   * Every finger currently down, so two of them can be told from one.
   *
   * A pinch is the one gesture that cannot be recognised from a single pointer
   * stream — it is *the relationship between two*. While two are down the map
   * does nothing else: no pan, no selection box, no tap when they lift, because
   * a pinch that also panned and then opened the wheel would be three things
   * happening for one gesture.
   */
  const touches = new Map<number, { x: number; y: number }>();
  let pinchDist = 0;
  /**
   * **This gesture has been a pinch, and stays one until the last finger goes.**
   *
   * "Two fingers are down" is not the same question as "was this a pinch", and
   * reading the first for the second is why the wheel popped up after every
   * zoom: lifting the *second* finger sees only one still down, and one finger
   * lifting without having moved is the definition of a tap. It never moved
   * because a pinch deliberately does not pan — so the map obligingly opened a
   * menu at the end of every zoom. A gesture is judged by what it has been.
   */
  let pinchGesture = false;

  /**
   * A mouse event arriving on the heels of a touch is the browser replaying
   * that touch, not a mouse. See `touchEcho` in `client/platform.ts` for what
   * that replay does to the wheel if it is allowed through.
   */
  const isPhantomMouse = (e: PointerEvent) => e.pointerType === 'mouse' && touchEcho();

  const clearHold = () => {
    if (touchHoldTimer !== null) { clearTimeout(touchHoldTimer); touchHoldTimer = null; }
  };

  /** The gap between the two fingers, and the point halfway between them. */
  const pinchOf = () => {
    const [a, b] = [...touches.values()];
    return {
      d: Math.hypot(a.x - b.x, a.y - b.y),
      mx: (a.x + b.x) / 2,
      my: (a.y + b.y) / 2,
    };
  };

  const tileAt = (px: number, py: number) => {
    const t = camera.screenToTile(px, py);
    const x = Math.max(0, Math.min(MAP_W - 1, Math.floor(t.x)));
    const y = Math.max(0, Math.min(MAP_H - 1, Math.floor(t.y)));
    return { x, y, index: idx(x, y), fx: t.x, fy: t.y };
  };

  /*
   * The wheel *is* our right-click menu, so the browser's own must never appear
   * on top of it. Listening on the document (not just the canvas) also covers
   * right-clicks that land on the wheel itself or on a HUD panel.
   */
  document.addEventListener('contextmenu', (e) => {
    if ($('hud').classList.contains('hidden')) return;   // menus keep their native menu
    e.preventDefault();
  });

  /*
   * **Two fingers zoom the map, never the page.**
   *
   * `touch-action: none` on the canvas says so, and Safari on iOS is the
   * browser that does not entirely listen: it keeps its own `gesture*` events
   * for pinch-zoom, and it has ignored `user-scalable=no` since iOS 10. A page
   * zoom is not a smaller version of a map zoom — it magnifies the interface
   * along with the world, so the wheel grows, the buttons drift away from where
   * they are drawn, and every measurement the game makes about the screen is
   * about a screen that is no longer there. Refusing the gesture leaves the
   * pinch to the pointer handlers, which move the camera and nothing else.
   */
  for (const type of ['gesturestart', 'gesturechange', 'gestureend']) {
    document.addEventListener(type, (e) => {
      if ($('hud').classList.contains('hidden')) return;   // menus may still zoom
      e.preventDefault();
    }, { passive: false });
  }

  canvas.addEventListener('pointerdown', (e: PointerEvent) => {
    // Capture keeps a drag alive when the cursor leaves the canvas, but it is a
    // nicety: it throws for pointers the browser does not consider active, and
    // letting that escape would take the entire click handler down with it.
    try { canvas.setPointerCapture?.(e.pointerId); } catch { /* not capturable */ }
    lx = e.clientX; ly = e.clientY;

    /* ------------------------------------------------------------ touch */
    if (isPhantomMouse(e)) return;
    if (e.pointerType === 'touch') {
      noteTouch();
      // stops the browser replaying this gesture as a mouse click a moment
      // later; `touch-action: none` on the canvas makes this safe
      e.preventDefault();
      touches.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (touches.size >= 2) {
        // a second finger cancels whatever the first was starting: this is a
        // pinch now, and it is the only thing it is
        pinchGesture = true;
        clearHold();
        panning = false;
        dragging = false;
        touchSelecting = false;
        wallDragging = false;
        state.wallDrag.length = 0;
        selBox.style.display = 'none';
        pinchDist = touches.size === 2 ? pinchOf().d : 0;
        return;
      }
      sx = e.clientX; sy = e.clientY;
      touchMoved = false;
      touchSelecting = false;
      clearHold();

      // the wall tool keeps its drawing gesture on a finger too: a drag traces a
      // line, a tap lays one segment, and either way the tool is put down after
      if (state.wallMode !== 0 && state.phase === Phase.Live && !state.defeated) {
        wallDragging = true;
        state.wallDrag.length = 0;
        const t = tileAt(e.clientX, e.clientY);
        lastWallX = t.x; lastWallY = t.y;
        addWallTile(t.x, t.y);
        return;
      }

      // hold still for a moment and the next drag draws a selection box
      touchHoldTimer = window.setTimeout(() => {
        touchHoldTimer = null;
        if (touchMoved) return;                 // already panning; too late
        touchSelecting = true;
        // hand the gesture over: `pointermove` checks `panning` first and would
        // otherwise keep dragging the world out from under the selection box
        panning = false;
        dragging = true;
        moved = false;
        sfx.play('click');
      }, TOUCH_HOLD_MS);

      panning = true;                            // until the hold says otherwise
      return;
    }

    if (e.button === 1 || (e.button === 0 && keys.has(' '))) {
      panning = true;
      return;
    }
    if (e.button === 0) {
      /*
       * A wall is **drawn**, not placed. While the wall tool is in hand the
       * left button traces a line across your own land instead of dragging a
       * selection box, and the whole line is sent as one order when you let go.
       */
      if (state.wallMode !== 0 && state.phase === Phase.Live && !state.defeated) {
        wallDragging = true;
        state.wallDrag.length = 0;
        const t = tileAt(e.clientX, e.clientY);
        lastWallX = t.x; lastWallY = t.y;
        addWallTile(t.x, t.y);
        return;
      }
      dragging = true;
      moved = false;
      sx = e.clientX; sy = e.clientY;
      return;
    }
    if (e.button === 2) {
      const t = tileAt(e.clientX, e.clientY);
      // Units first: while something is selected, right click is still "go there".
      if (state.selected.size > 0) {
        commandSelection(camera, t, e.clientX, e.clientY);
        return;
      }
      /*
       * A market in hand: right-click names its trade partner. Anywhere that is
       * not a market hands the route back to the merchant, which is the default
       * and is how you undo this.
       */
      if (state.selectedMarket >= 0) {
        // a partner has to be the same kind of house: road to road, sea to sea
        const held = state.structures.get(state.selectedMarket);
        const target = marketNear(t.index, false, held ? held.type : -1);
        net.send({ t: 'trade', id: state.selectedMarket, target: target ? target.id : -1 });
        state.selectedMarket = -1;
        sfx.play('coin');
        return;
      }
      /*
       * Nothing selected: ask the tile what can be done on it.
       *
       * **A team-mate's ground comes through here too, now.** It used to be
       * intercepted above and send them people on the spot, with nothing to
       * confirm and nothing to read — one twitch of the mouse over a friendly
       * border and a third of your country had marched off. The gesture and the
       * arithmetic are unchanged; they are the middle of the wheel (`buildHub`),
       * which names who it is for and what it will cost you before you take it,
       * exactly as attacking a stranger does.
       */
      if (radialOpen()) closeRadial();
      else openRadial(t.index, e.clientX, e.clientY);
    }
  });

  window.addEventListener('pointermove', (e) => {
    if (isPhantomMouse(e)) return;
    if (e.pointerType === 'touch') noteTouch();
    cursor.x = e.clientX; cursor.y = e.clientY;
    const dx = e.clientX - lx, dy = e.clientY - ly;
    lx = e.clientX; ly = e.clientY;
    state.hoverTile = tileAt(e.clientX, e.clientY).index;
    state.cursorX = e.clientX;
    state.cursorY = e.clientY;
    // only the world gets a hover card; over a panel the cursor is doing UI work
    state.overWorld = (e.target as HTMLElement | null)?.id === 'game';

    if (e.pointerType === 'touch') {
      /*
       * **Only fingers that went down on the map are the map's business.**
       *
       * These listeners are on the window, so they hear every finger on the
       * page — including one dragging a panel or resting on the wheel. Reading
       * those as map gestures is how a tap on a wheel slice came to be treated
       * as a tap on the ground behind it. `touches` holds exactly the fingers
       * that started on the canvas, so it is also the guest list.
       */
      if (!touches.has(e.pointerId)) return;
      const held = touches.get(e.pointerId)!;
      held.x = e.clientX;
      held.y = e.clientY;
      if (touches.size === 2) {
        const { d, mx, my } = pinchOf();
        // a ratio, not a step: the map grows out of the point between the
        // fingers by exactly as much as they have moved apart
        if (pinchDist > 0 && d > 0) camera.zoomBy(d / pinchDist, mx, my);
        pinchDist = d;
        return;
      }
      if (!touchMoved) {
        // past the slop it is a drag, and no longer a candidate for a tap; if
        // the hold has not fired by now it never will
        if (Math.abs(e.clientX - sx) + Math.abs(e.clientY - sy) > TOUCH_SLOP) {
          touchMoved = true;
          if (!touchSelecting) clearHold();
        }
      }
    }

    if (panning) {
      camera.panScreen(dx, dy);
      return;
    }
    if (wallDragging) {
      const t = tileAt(e.clientX, e.clientY);
      if (t.x !== lastWallX || t.y !== lastWallY) {
        // fill in every tile between the last sample and this one, or a quick
        // sweep of the mouse leaves a wall full of doorways nobody asked for
        traceWall(lastWallX, lastWallY, t.x, t.y);
        lastWallX = t.x; lastWallY = t.y;
      }
      return;
    }
    if (dragging) {
      if (Math.abs(e.clientX - sx) + Math.abs(e.clientY - sy) > 6) moved = true;
      if (moved) {
        selBox.style.display = 'block';
        selBox.style.left = `${Math.min(sx, e.clientX)}px`;
        selBox.style.top = `${Math.min(sy, e.clientY)}px`;
        selBox.style.width = `${Math.abs(e.clientX - sx)}px`;
        selBox.style.height = `${Math.abs(e.clientY - sy)}px`;
      }
    }
  });

  window.addEventListener('pointerup', (e) => {
    if (isPhantomMouse(e)) return;
    if (e.pointerType === 'touch') {
      noteTouch();
      /*
       * **A finger that never touched the map cannot have tapped it.**
       *
       * The one that broke Attack. This listener is on the window, so lifting a
       * finger off a wheel slice arrives here too — and with the flags left
       * over from the tap that opened the wheel (`touchMoved` false, nothing
       * selected) it read as a fresh tap on the ground, whose meaning with the
       * wheel already open is *close the wheel*. The menu shut on `pointerup`,
       * a hair before the slice's own click, so the press went nowhere. The
       * canvas is what fills `touches`; a pointer missing from it went down on
       * something else, and belongs to whatever that was.
       */
      if (!touches.has(e.pointerId)) return;
      const wasPinching = pinchGesture;
      touches.delete(e.pointerId);
      if (touches.size === 0) pinchGesture = false;
      clearHold();
      /*
       * Lifting one finger of a pinch is not a tap, and the finger still down
       * is not the start of a new gesture — it has already been dragging. So a
       * pinch ends silently and the next `pointerdown` starts afresh.
       */
      if (wasPinching) { pinchDist = 0; panning = false; return; }
      const wasSelecting = touchSelecting;
      const wasTap = !touchMoved && !wasSelecting;
      touchSelecting = false;
      panning = false;

      if (wallDragging) {
        // falls through to the shared wall handler below, which sends the order
        // and puts the tool down
      } else if (wasSelecting) {
        dragging = false;
        selBox.style.display = 'none';
        if (moved) {
          boxSelect(camera, Math.min(sx, e.clientX), Math.min(sy, e.clientY),
            Math.max(sx, e.clientX), Math.max(sy, e.clientY));
        }
        return;
      } else if (wasTap) {
        /*
         * **One tap does whatever the situation says it should.**
         *
         * With soldiers in hand it is an order — the same order a right-click
         * gives on a desktop, down to hunting a foreign unit and breaking a
         * rampart. With nothing in hand it opens the wheel, which is where
         * building, recruiting and attacking already live: a finger therefore
         * needs no second menu, and nothing about the game had to be simplified
         * to fit one.
         */
        const t = tileAt(e.clientX, e.clientY);
        if (state.selected.size > 0) commandSelection(camera, t, e.clientX, e.clientY);
        else if (state.buildType >= 0 || state.trainType >= 0 || state.boatMode
          || state.powerMode || getPingMode() >= 0 || state.phase === Phase.Spawn) {
          // a tool is in hand, or a homeland is being chosen: a tap is that
          leftClick(camera, e.clientX, e.clientY, tileAt);
        } else if (radialOpen()) closeRadial();
        else openRadial(t.index, e.clientX, e.clientY);
        return;
      } else {
        return;                                   // it was a pan; nothing else to do
      }
    }
    if (panning && (e.button === 1 || e.button === 0)) { panning = false; return; }
    if (wallDragging) {
      wallDragging = false;
      const drew = state.wallDrag.length > 0;
      if (drew) {
        net.send({
          t: 'wall',
          tiles: [...state.wallDrag],
          gate: state.wallMode === WallKind.Gate,
        });
        sfx.play('build');
      }
      state.wallDrag.length = 0;
      /*
       * **One line, one order, and then the tool is down again** — hold shift
       * while you let go to keep it in hand for the next stretch.
       *
       * Only when something was actually built, though. A gesture that drew
       * nothing — over the sea, over a neighbour's fields, over a rampart that
       * already stands there — is a *miss*, and taking the tool out of the
       * player's hand for missing is the wrong answer twice over: they still
       * want it, and on a phone the only way to get it back is the wheel.
       */
      if (drew && !keys.has('shift')) state.wallMode = 0;
      if (!drew) {
        notice('Nothing was drawn — a rampart goes on your own dry ground, '
          + 'and not where one already stands.', 'bad');
      }
      return;
    }
    if (!dragging) return;
    dragging = false;
    selBox.style.display = 'none';

    if (moved) {
      boxSelect(camera, Math.min(sx, e.clientX), Math.min(sy, e.clientY), Math.max(sx, e.clientX), Math.max(sy, e.clientY));
      return;
    }
    leftClick(camera, e.clientX, e.clientY, tileAt);
  });

  // a finger the browser takes away — a system gesture, a call arriving — must
  // not be left in the map as a phantom still holding a pinch open
  window.addEventListener('pointercancel', (e) => {
    if (e.pointerType !== 'touch') return;
    touches.delete(e.pointerId);
    if (touches.size < 2) pinchDist = 0;
    if (touches.size === 0) pinchGesture = false;
    clearHold();
    panning = false;
    dragging = false;
    touchSelecting = false;
    selBox.style.display = 'none';
  });

  canvas.addEventListener('wheel', (e) => {
    e.preventDefault();
    /*
     * **With the power armed, the wheel sizes the blast instead of the view.**
     *
     * The one moment in the game where the wheel means something other than
     * zoom — and it has to, because choosing how wide a bite to take is the
     * decision this weapon poses, and it must be made with the same hand that
     * is about to click the target. Zooming while aiming a doomsday weapon is
     * not something anybody was going to do anyway.
     */
    if (state.powerMode) {
      // a share of the current size, so one turn of the wheel feels the same at
      // sixteen tiles as it does at a hundred and fifty
      const k = e.deltaY < 0 ? 1 + RACE_POWER_AREA_STEP : 1 / (1 + RACE_POWER_AREA_STEP);
      state.powerRadius = Math.max(RACE_POWER_AREA_MIN,
        Math.min(RACE_POWER_AREA_MAX, Math.round(state.powerRadius * k)));
      sfx.play('click');
      return;
    }
    camera.zoomAt(-e.deltaY, e.clientX, e.clientY);
  }, { passive: false });

  /* ---------------------------------------------------------- keyboard */

  window.addEventListener('keydown', (e) => {
    const active = document.activeElement;
    if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA')) return;
    const k = e.key.toLowerCase();
    keys.add(e.key === ' ' ? ' ' : k);

    if (e.key === 'Tab') {
      toggleCinematic();   // hide the whole interface and look at the world
      e.preventDefault();
      return;
    }
    if (e.key === 'Escape') {
      dropTool();
      closeRadial();
      closeAidPanel();
      exitFullscreen();
      state.selected.clear();
      state.selectedMarket = -1;
      return;
    }
    if (e.key === 'Delete' || e.key === 'Backspace') {
      if (state.selected.size > 0) {
        net.send({ t: 'disband', ids: [...state.selected] });
        state.selected.clear();
        sfx.play('death');
      }
      e.preventDefault();
      return;
    }
    if (k >= '1' && k <= '9') {
      state.ratio = Number(k) / 10;
      syncRatio();
      return;
    }
    if (k === '0') { state.ratio = 1; syncRatio(); return; }

    /*
     * **Training keys never touch WASD.**
     *
     * `W` used to raise an Archer, and `W` also pans the camera up — so
     * scrolling north queued a unit, and *holding* W queued one per key repeat.
     * The letters now avoid the movement keys entirely (Q E R T), and a held
     * key is ignored: an order is something you press, not something you lean
     * on. Arrow keys pan too, so nothing was lost by moving off W.
     *
     * Read from `UNIT_DEFS[...].hotkey` rather than hard-coded, which also
     * fixes a quieter bug: the four cases named the *human* roster, so a dwarf
     * pressing Q was told no building can train a Knight. Now each people's own
     * four answer to the letters printed on their own buttons.
     */
    if (!e.repeat && trainByHotkey(k)) return;

    switch (k) {
      case 'b': state.buildType = state.buildType >= 0 ? -1 : 1; state.trainType = -1; break;
      case 'v': toggleWallMode(WallKind.Rampart); break;
      case 'n': toggleWallMode(WallKind.Gate); break;
      case 'h': toggleBoatMode(); break;
      case 'm': toggleSettings(); break;
      case 'g': toggleFullscreen(); break;
      case 'f': {
        const t = camera.screenToTile(cursor.x, cursor.y);
        for (const id of state.selected) {
          const u = state.units.get(id);
          if (u && u.type === UnitType.Wizard) {
            net.send({ t: 'meteor', id, x: t.x, y: t.y });
            sfx.play('meteor');
            break;
          }
        }
        break;
      }
      // hold your fire: the selection stops razing everything it walks past
      case 'x': toggleSpareBuildings(); break;
      case ' ': {
        const me = state.players.get(state.you);
        if (me && me.count > 0) {
          camera.userControlled = false;
          camera.glideTo(me.sumX / me.count, me.sumY / me.count);
          camera.userControlled = true;   // one trip home, then hands off again
        }
        break;
      }
    }
  });

  window.addEventListener('keyup', (e) => {
    keys.delete(e.key === ' ' ? ' ' : e.key.toLowerCase());
  });

  window.addEventListener('blur', () => keys.clear());
}

/** One of your own buildings on this tile — a wall may not share the ground. */
function structureOnTile(tile: number): boolean {
  for (const s of state.structures.values()) if (s.tile === tile) return true;
  return false;
}

/**
 * Somebody standing on this tile, by the same reckoning the server uses.
 *
 * Only what is on screen is in `state.units` — snapshots are viewport-filtered
 * — but so is the line you are drawing, so the two coincide where it matters.
 */
function unitOnTile(x: number, y: number): boolean {
  for (const u of state.units.values()) {
    if (Math.floor(u.tx) === x && Math.floor(u.ty) === y) return true;
  }
  return false;
}

/**
 * **What the line will cost by the time it reaches this tile — priced the way
 * the server prices it.**
 *
 * Every twenty segments the next one gets dearer, and the count that decides
 * that is the *running* one: a forty-tile drag crosses a price boundary inside
 * itself. Converting a rampart into a gate is paid for but does not add to the
 * count, which is why this walks the line rather than multiplying by its
 * length.
 */
function dragAffordable(candidateIsNew: boolean): boolean {
  const gate = state.wallMode === WallKind.Gate;
  const total = new Array(RES_COUNT).fill(0);
  let raised = 0;
  const charge = (isNew: boolean) => {
    const price = wallCost(state.self.wallCount + raised, gate);
    for (let r = 0; r < RES_COUNT; r++) total[r] += price[r] || 0;
    if (isNew) raised++;
  };
  for (const t of state.wallDrag) charge(!state.walls.has(t));
  charge(candidateIsNew);
  return priceWithGold(total, state.self.res).ok;
}

/**
 * Adds one tile to the line being drawn — **only if a wall will actually end up
 * there.**
 *
 * The ghost line is a promise, and a promise the order cannot keep is worse
 * than no preview at all: white stonework was drawn across a neighbour's
 * fields, over open water, through your own farms and over your own soldiers,
 * and then the wall came back with holes in it exactly where the picture had
 * been most confident. The server has always skipped those tiles and built the
 * rest — that half was never broken — so all that was missing was for the
 * drawing to know the same rules the building does.
 *
 * They are the rules from `canWall` on the server, in the same order, plus the
 * price: a drag that outruns your treasury now stops where the money will,
 * instead of showing you forty tiles of wall you can afford ten of.
 */
function addWallTile(x: number, y: number) {
  if (state.wallDrag.length >= WALL_MAX_PER_ORDER) return;
  if (x < 0 || y < 0 || x >= MAP_W || y >= MAP_H) return;
  const tile = idx(x, y);
  if (state.wallDrag.includes(tile)) return;
  if (state.owner[tile] !== state.you) return;
  // a standing segment is still a candidate when you are drawing the *other*
  // kind: dragging the gate tool across your own rampart cuts doors in it
  const standing = state.walls.get(tile);
  if (standing && standing.kind === state.wallMode) return;
  if (!standing) {
    if (state.terrain && state.terrain[tile] < Terrain.Plains) return;
    if (structureOnTile(tile)) return;
    if (unitOnTile(x, y)) return;
  } else if (state.wallMode === WallKind.Rampart && unitOnTile(x, y)) {
    // bricking a gateway up with somebody standing in it walls them in, so the
    // server refuses it — cutting a gate *into* a rampart is always allowed
    return;
  }
  if (!dragAffordable(!standing)) return;
  state.wallDrag.push(tile);
}

/**
 * A four-connected line between two mouse samples.
 *
 * Four-connected and not eight: a diagonal step leaves a diagonal gap, and a
 * unit walking through a gap does not care that the wall *looks* continuous.
 * Stepping one axis at a time gives a line nothing can slip through.
 */
function traceWall(x0: number, y0: number, x1: number, y1: number) {
  let x = x0, y = y0;
  const dx = Math.abs(x1 - x0), sx = x0 < x1 ? 1 : -1;
  const dy = -Math.abs(y1 - y0), sy = y0 < y1 ? 1 : -1;
  let err = dx + dy;
  for (let guard = 0; guard < 1024; guard++) {
    if (x === x1 && y === y1) return;
    const e2 = 2 * err;
    if (e2 >= dy) { err += dy; x += sx; } else { err += dx; y += sy; }
    addWallTile(x, y);
  }
}

function syncRatio() {
  const el = document.getElementById('ratio') as HTMLInputElement;
  el.value = String(Math.round(state.ratio * 100));
  document.getElementById('ratio-label')!.textContent = `${el.value}%`;
}

/**
 * The trading house on or beside this tile — anybody's, or only your own.
 *
 * Markets **and** harbours, because both take a trade route and both take it
 * the same way. `kind` narrows the search to one of them, which is what naming
 * a partner needs: a market sells to a market by road and a harbour to a
 * harbour by sea, and offering the wrong one would be offering a route the
 * server is only going to refuse.
 *
 * Two tiles of slack, for the same reason building has it: a market is one
 * tile on a map you are usually looking at from a long way off, and "the one I
 * clicked at" is what the player meant.
 */
function marketNear(tile: number, mineOnly: boolean, kind = -1) {
  if (tile < 0) return null;
  const x = tile % MAP_W, y = (tile / MAP_W) | 0;
  let best: { id: number; owner: number; type: number } | null = null;
  let bestD = 5;                       // squared: two tiles out, corners included
  for (const s of state.structures.values()) {
    if (kind >= 0 ? s.type !== kind
      : s.type !== StructureType.Market && s.type !== StructureType.Harbour) continue;
    if (mineOnly && s.owner !== state.you) continue;
    const sx = s.tile % MAP_W, sy = ((s.tile / MAP_W) | 0);
    const d = (sx - x) ** 2 + (sy - y) ** 2;
    if (d < bestD) { bestD = d; best = s; }
  }
  return best;
}

/**
 * Somebody else's unit under the cursor, or -1.
 *
 * Screen-space rather than tile-space, because this answers "did they click
 * *that*" and a unit is drawn at a size the map does not know about. Your own
 * soldiers and your team-mates' are skipped: right-clicking through your own
 * front line to reach the enemy behind it is the normal thing to want, and
 * nobody ever means to declare war on their own side.
 */
const RAID_PICK_PX = 22;
function foreignUnitAt(camera: Camera, px: number, py: number): number {
  let best = -1;
  let bestD = RAID_PICK_PX * RAID_PICK_PX;
  for (const u of state.units.values()) {
    if (u.owner === state.you || isFriendly(u.owner)) continue;
    const s = camera.worldToScreen(u.tx * ART, u.ty * ART);
    const dx = s.x - px, dy = s.y - py;
    const d2 = dx * dx + dy * dy;
    if (d2 < bestD) { bestD = d2; best = u.id; }
  }
  return best;
}

/**
 * **Send whatever is in hand to the tile that was pointed at — then let go of it.**
 *
 * One function for two gestures: a right-click on a desktop and a tap on a
 * touch screen. They mean exactly the same thing, so they had better not drift.
 *
 * **The selection is dropped afterwards, and that is the change.** Orders used
 * to leave the squad in hand, which sounds convenient and is the opposite: the
 * *next* click anywhere on the map was then another order, so looking at
 * somewhere else sent your army there. Holding a squad is a state a player has
 * to remember they are in, and there was nothing on screen to remind them.
 * Issuing an order is the natural moment to end it — and re-selecting is one
 * drag, which is cheaper than an army marching somewhere you did not mean.
 */
function commandSelection(
  camera: Camera,
  t: { index: number; fx: number; fy: number },
  px: number,
  py: number,
) {
  // a transport already at sea is re-aimed at a new shore instead
  const boats: number[] = [];
  const marchers: number[] = [];
  for (const id of state.selected) {
    const u = state.units.get(id);
    if (u && u.type === UnitType.Boat) boats.push(id); else marchers.push(id);
  }
  if (boats.length) net.send({ t: 'sail', ids: boats, tile: t.index });
  /*
   * **Pointing at somebody else's soldier is an order to hunt it.**
   *
   * The same gesture as a march, aimed at a *unit* rather than at ground — which
   * is how you rob a caravan belonging to a kingdom you share no border with. It
   * declares war (the server says so plainly), so it is deliberately hard to do
   * by accident: the quarry has to be under the pointer, and your own men and
   * your team-mates' are never quarry.
   *
   * **And pointing at somebody else's wall is an order to break it.** A rampart
   * is the one thing population cannot touch, so only soldiers bring one down,
   * and soldiers only engage a realm you have already fought — without this a
   * neighbour could close a border you had never crossed and you would have no
   * way to open it, or to start the war that would let you.
   */
  if (marchers.length) {
    const quarry = foreignUnitAt(camera, px, py);
    const wall = state.walls.get(t.index);
    if (quarry >= 0) net.send({ t: 'raid', ids: marchers, target: quarry });
    else if (wall && wall.owner !== state.you && !isFriendly(wall.owner)) {
      net.send({ t: 'siege', ids: marchers, tile: t.index });
    } else net.send({ t: 'move', ids: marchers, x: t.fx, y: t.fy });
  }
  state.selected.clear();
  sfx.play('click');
}

function boxSelect(camera: Camera, x0: number, y0: number, x1: number, y1: number) {
  state.selected.clear();
  for (const u of state.units.values()) {
    if (u.owner !== state.you) continue;
    const s = camera.worldToScreen(u.tx * ART, u.ty * ART);
    if (s.x >= x0 && s.x <= x1 && s.y >= y0 && s.y <= y1) state.selected.add(u.id);
  }
  if (state.selected.size) sfx.play('click');
}

function leftClick(
  camera: Camera,
  px: number,
  py: number,
  tileAt: (x: number, y: number) => { x: number; y: number; index: number; fx: number; fy: number },
) {
  const t = tileAt(px, py);

  // an open wheel swallows the next click: dismiss it, do not also attack
  if (radialOpen()) { closeRadial(); return; }
  // a fallen kingdom can look but not touch
  if (state.defeated) return;

  if (state.phase === Phase.Spawn) {
    /*
     * A choice, not a commitment — and **the camera does not move for it.**
     *
     * Clicking again moves your kingdom, so this phase is for shopping around:
     * try a river mouth, look at who settled next door, try the mountains
     * instead. A camera that swooped in on every click would fight that, and
     * would hide the very thing you are shopping for. The map takes you home
     * once, when the war actually starts (see `main.ts`).
     */
    net.send({ t: 'spawn', tile: t.index });
    sfx.play('click');
    return;
  }

  const ping = getPingMode();
  if (ping >= 0) {
    net.send({ t: 'ping', x: t.fx, y: t.fy, kind: ping });
    setPingMode(-1);
    sfx.play('ping');
    return;
  }

  /*
   * The race power is armed: this click is where it lands — if it can.
   *
   * The reach is measured from your realm's centroid, and it used to be checked
   * only on the server: an out-of-range click disarmed the power, played the
   * strike sound and produced nothing at all, which reads exactly like a broken
   * weapon. Refusing it here keeps the power armed so the next click can be a
   * better one, and says what is wrong.
   */
  if (state.powerMode) {
    const dx = t.fx - state.self.centroidX, dy = t.fy - state.self.centroidY;
    if (dx * dx + dy * dy > RACE_POWER_RANGE * RACE_POWER_RANGE) {
      notice(`That is beyond your reach — a calling carries ${RACE_POWER_RANGE} tiles `
        + 'from the heart of your realm. Take ground towards them first.', 'bad');
      sfx.play('error');
      return;
    }
    net.send({ t: 'power', x: t.fx, y: t.fy, r: state.powerRadius });
    state.powerMode = false;
    sfx.play('meteor');
    return;
  }

  if (state.buildType >= 0) {
    net.send({ t: 'build', s: state.buildType, tile: t.index });
    sfx.play('build');
    if (!keys.has('shift')) state.buildType = -1;
    return;
  }

  // pick the destination first, then the unit gets built in the closest barracks
  if (state.trainType >= 0) {
    net.send({ t: 'train', u: state.trainType, x: t.fx, y: t.fy });
    sfx.play('train');
    if (!keys.has('shift')) state.trainType = -1;
    return;
  }

  if (state.boatMode) {
    net.send({ t: 'boat', tile: t.index, ratio: state.ratio });
    sfx.play('click');
    if (!keys.has('shift')) state.boatMode = false;
    return;
  }

  /*
   * Attacking is a **double click**, or the middle of the right-click wheel.
   * A single left click only ever clears the selection — losing half your army
   * because you clicked to look at something was the single worst misclick in
   * the game.
   */
  const owner = state.owner[t.index];
  if (owner === state.you) {
    /*
     * Your own ground. A click on one of your Markets picks it up so a trade
     * route can be set on it; a click anywhere else on your land is "never
     * mind", and drops whatever was in hand.
     */
    const market = marketNear(t.index, true);
    state.selected.clear();
    state.selectedMarket = market ? market.id : -1;
    if (market) {
      sfx.play('click');
      notice(market.type === StructureType.Harbour
        ? 'Harbour in hand — right-click another harbour to ship to it, or your own land to free it.'
        : 'Market in hand — right-click another market to sell to it, or your own land to free it.');
    }
    lastClickTile = -1;
    return;
  }

  /*
   * A team-mate's ground, with nothing in hand. There is no attack to make here
   * — double-clicking an ally has always been a click into the void — so the
   * click opens the aid cart instead. That is goods; the right-click wheel over
   * the same ground is men.
   */
  if (owner !== 0 && isFriendly(owner) && state.selected.size === 0) {
    openAidPanel(owner);
    sfx.play('click');
    lastClickTile = -1;
    return;
  }

  const now = performance.now();
  if (lastClickTile === t.index && now - lastClickAt < DOUBLE_CLICK_MS) {
    lastClickTile = -1;
    net.send({ t: 'attack', tile: t.index, ratio: state.ratio });
    sfx.play('attack');
    return;
  }
  lastClickTile = t.index;
  lastClickAt = now;
  state.selected.clear();
  // say it a couple of times, then trust the player to have learned it
  if (attackHints > 0) {
    attackHints--;
    notice('Double-click to attack — or right-click for the wheel.');
  }
}

let lastClickTile = -1;
let lastClickAt = 0;
let attackHints = 3;
